<section class="go-container">

  <go-card class="go-column go-column--100" id="implementing-selection">
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap">Implementing Selection</h2>
      <go-copy cardId="implementing-selection" goCopyCardLink></go-copy>
    </ng-container>
    <div class="go-container" go-card-content>

      <div class="go-column go-column--100">
        <p class="go-body-copy">
          By default, selection is disabled. To add row selection capabilities to the table, there are two properties of the <code class="code-block--inline">GoTableConfig</code>
          that must be set, <code class="code-block--inline">selectable</code> and <code class="code-block--inline">selectBy</code>.
        </p>

        <code [highlight]="tableSelectConfig"></code>
      </div>

      <div class="go-column go-column--100">
        <h4 class="go-heading-4">selectable</h4>
        <p class="go-body-copy">
          This property enables selection within the table. Without this enabled, selection will not be possible.
        </p>
      </div>

      <div class="go-column go-column--100">
        <h4 class="go-heading-4">selectBy</h4>
        <p class="go-body-copy go-body-copy--no-margin">
          This property determines which column will be used to keep track of row selection. It corresponds to one of the fields in the columns.
          Typically, this should be an id, or some other unique identifier in the data set.
        </p>
      </div>

    </div>
  </go-card>

  <go-card class="go-column go-column--100" id="important-notes">
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap">Imporant Notes</h2>
      <go-copy cardId="important-notes" goCopyCardLink></go-copy>
    </ng-container>
    <ng-container go-card-content>
      <p class="go-body-copy">
        <strong>Selection is persistant.</strong> This means that if the user changes the page or sorts the data, previous selections will persist through these actions.
        This applies to both client side <strong>and server side</strong> implementations.
      </p>
    </ng-container>
  </go-card>

  <go-card class="go-column go-column--100" id="interactive-selection">
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap">Interacting with the Selection</h2>
      <go-copy cardId="interactive-selection" goCopyCardLink></go-copy>
    </ng-container>
    <ng-container go-card-content>
      <p class="go-body-copy">
        There are two ways to interact with the selected items of the table:
      </p>
      <ol class="go-ordered-list">
        <li class="go-ordered-list__item">
          By binding to the table's selection event outputs (the subject of this card).
        </li>
        <li class="go-ordered-list__item">
          By using an element reference to the table (see the "Element Reference Interection" card below).
        </li>
      </ol>
      <h4 class="go-heading-4">Binding to the table's selection events</h4>

      <p class="go-body-copy">There are two outputs that emit selection events from the table</p>
      <code [highlight]="tableSelectionEventOutputs"></code>

      <p class="go-body-copy">
        The interfaces for the <code class="code-block--inline">RowSelectionEvent</code> and the
        <code class="code-block--inline">SelectionState</code> provide more information about the
        structure of these events:
      </p>
      <code [highlight]="tableSelectionModels"></code>
      <p class="go-body-copy">
        To summarize: in selection mode, the user is selecting individual rows. This means that these selected rows are being added to the selectedRows array.
        In deselection mode, the user has checked the 'check all' check box and is now removing items from the selection; we call this deselection.
        In this case, items are added to the deselectedRows array as the user unchecks them.
      </p>

      <h4 class="go-heading-4">example.html</h4>
      <code [highlight]="tableSelectionExample_html"></code>
      <h4 class="go-heading-4">example.ts</h4>
      <code [highlight]="tableSelectionExample_ts"></code>

      <p class="go-body-copy">
        Interact with the table below to see how the selection works.
      </p>
    </ng-container>
  </go-card>

  <go-table
    class="go-column go-column--100"
    [tableConfig]="interactiveTableConfig"
    tableTitle="Interactive Example of Selection States"
    [showTableActions]="true"
    (rowSelectionEvent)="tableRowEvent($event)"
    (selectAllEvent)="selectAllEvent($event)"
    #interactiveTable>
    <ng-container go-table-actions>
      <go-button (handleClick)="interactiveTableState()">State</go-button>
    </ng-container>
    <go-table-column field="id" title="ID"></go-table-column>
    <go-table-column field="name.first" title="First Name"></go-table-column>
    <go-table-column field="name.last" title="Last Name"></go-table-column>
    <go-table-column field="email" title="Email"></go-table-column>
    <go-table-column field="gender" title="Gender"></go-table-column>
    <go-table-column field="ip_address" title="IP Address"></go-table-column>
  </go-table>

  <go-card class="go-column go-column--100" id="element-reference">
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap">Element Reference Interaction</h2>
      <go-copy cardId="element-reference" goCopyCardLink></go-copy>
    </ng-container>
    <ng-container go-card-content>
      <p class="go-body-copy">
        This approach should be used when an action is performed after a user has finished their selecting (ex. button click).
      </p>
      <h4 class="go-heading-4">example.html</h4>
      <code [highlight]="eleRef_html"></code>
      <h4 class="go-heading-4">example.ts</h4>
      <code [highlight]="eleRef_ts" class="code-block--no-bottom-margin"></code>
    </ng-container>
  </go-card>

  <go-table
    class="go-column go-column--100"
    [tableConfig]="eleRefTableConfig"
    tableTitle="Element Reference Example"
    [showTableActions]="true"
    #eleRefTable>
    <ng-container go-table-actions>
      <go-button (handleClick)="getSelectionState()">Selection State</go-button>
    </ng-container>
    <go-table-column field="id" title="ID"></go-table-column>
    <go-table-column field="name.first" title="First Name"></go-table-column>
    <go-table-column field="name.last" title="Last Name"></go-table-column>
    <go-table-column field="email" title="Email"></go-table-column>
    <go-table-column field="gender" title="Gender"></go-table-column>
    <go-table-column field="ip_address" title="IP Address"></go-table-column>
  </go-table>

  <go-card class="go-column go-column--100" id="row-event">
      <ng-container go-card-header>
        <h2 class="go-heading-2 go-heading--no-wrap">Row Event Interaction</h2>
        <go-copy cardId="row-event" goCopyCardLink></go-copy>
      </ng-container>
      <ng-container go-card-content>
        <p class="go-body-copy">
          This approach should be used when an action necessary upon individual row selection or deselection.
        </p>
        <h4 class="go-heading-4">example.html</h4>
        <code [highlight]="rowEvent_html"></code>
        <h4 class="go-heading-4">example.ts</h4>
        <code [highlight]="rowEvent_ts" class="code-block--no-bottom-margin"></code>
      </ng-container>
    </go-card>

  <go-table
    class="go-column go-column--100"
    [tableConfig]="rowEventTableConfig"
    tableTitle="Row Event Example"
    (rowSelectionEvent)="tableRowEvent($event)">
    <go-table-column field="id" title="ID"></go-table-column>
    <go-table-column field="name.first" title="First Name"></go-table-column>
    <go-table-column field="name.last" title="Last Name"></go-table-column>
    <go-table-column field="email" title="Email"></go-table-column>
    <go-table-column field="gender" title="Gender"></go-table-column>
    <go-table-column field="ip_address" title="IP Address"></go-table-column>
  </go-table>

  <go-card class="go-column go-column--100" id="preselect-options">
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap">Preselect All Rows</h2>
      <go-copy cardId="preselect-options" goCopyCardLink></go-copy>
    </ng-container>
    <ng-container go-card-content>
      <p class="go-body-copy">
        There may be times where you want all options in a table to be preselected and the user to start
        with deselecting options instead of selecting them. This can be accomplished through the
        <code class="code-block--inline">preselected</code> option in the <code class="code-block--inline">GoTableConfig</code>.
      </p>
      <h4 class="go-heading-4">example.html</h4>
      <code [highlight]="preselectedTable_html"></code>
      <h4 class="go-heading-4">example.ts</h4>
      <code [highlight]="preselectedTable_ts" class="code-block--no-bottom-margin"></code>
    </ng-container>
  </go-card>

  <go-table
    class="go-column go-column--100"
    [tableConfig]="preselectedTableConfig"
    tableTitle="Preselect All Rows">
    <go-table-column field="id" title="ID"></go-table-column>
    <go-table-column field="name.first" title="First Name"></go-table-column>
    <go-table-column field="name.last" title="Last Name"></go-table-column>
    <go-table-column field="email" title="Email"></go-table-column>
    <go-table-column field="gender" title="Gender"></go-table-column>
    <go-table-column field="ip_address" title="IP Address"></go-table-column>
  </go-table>
</section>
